import styled from '@emotion/styled'
import { components } from 'api/goodtrust/swagger'
import { Avatar, AvatarOwner } from 'components/avatar/Avatar'
import { Button } from 'components/button/Button'
import { Icon } from 'components/icon'
import { Spacer } from 'components/spacer/Spacer'
import { HStack } from 'components/Stack'
import { Text } from 'components/Typography'
import {
  SSecondaryButton,
  SWizardGridCardHeader,
  SWizardGridCardImage,
  SWizardGridCardShareArea,
  SWizardGridCardShareItem,
  SWizardGridCardShareWrapper,
  SWizardGridCardTitle,
  SWizardGridCardWrapper,
  SWizardGridLockedWrapper,
} from './WizardGridCard.styled'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { FC, forwardRef, ReactNode } from 'react'
import { colors } from 'utils/colors'
import { BREAKPOINTS, pxToRem, pxToRemUnit } from 'utils/styled'

export const SWizardGrid = styled.div<{ multiRow?: boolean }>`
  display: grid;
  grid-gap: 1rem;
  grid-auto-flow: ${({ multiRow }) => (multiRow ? 'unset' : 'column')};
  grid-template-columns: repeat(auto-fill, minmax(12.5rem, 1fr));
  grid-auto-rows: 1fr;

  @media ${BREAKPOINTS.SM.max} {
    grid-auto-flow: row;
  }
`

type CardStatusDescription = {
  variant: 'success' | 'warning'
  label: string
}

export const useCardStatusComplete: () => CardStatusDescription = () => {
  const { t } = useTranslation()
  return {
    variant: 'success',
    label: t('common.complete'),
  }
}

export const useCardStatusMissingTC: () => CardStatusDescription = () => {
  const { t } = useTranslation()
  return {
    variant: 'warning',
    label: t('common.missing_trusted_contact'),
  }
}

export const CardStatus: FC<React.PropsWithChildren<{ status: CardStatusDescription }>> = ({
  status,
}) => {
  return (
    <HStack space={pxToRemUnit(6)}>
      <Icon
        color={status.variant === 'success' ? colors.green[200] : colors.gray[500]}
        name={status.variant === 'success' ? 'Check' : 'Warning'}
        size={pxToRemUnit(12)}
      />
      <Text variant="bodyMedium">{status.label}</Text>
    </HStack>
  )
}

export const WizardGridCard = forwardRef<
  HTMLButtonElement,
  {
    ['data-testid']?: string
    className?: string
    image: ReactNode
    title?: ReactNode
    brief?: ReactNode
    subtitle?: ReactNode
    entityType?: ReactNode
    openMenu?: () => void
    isLoading?: boolean
    locked?: boolean | null
    status?: CardStatusDescription
    onClick?: (event?: React.MouseEvent<HTMLElement>) => void
    noImageHeight?: boolean
    noLineClamp?: boolean
  }
>((props, ref) => {
  return (
    <SWizardGridCardWrapper>
      <SSecondaryButton
        type="button"
        variant="secondary"
        data-testid={props['data-testid']}
        className={props.className}
        loading={!!props.isLoading}
        disabled={props.locked ?? undefined}
        onClick={props.onClick}
        ref={ref}
      >
        <HStack css={{ width: '100%', justifyContent: 'space-between', alignItems: 'start' }}>
          <SWizardGridCardImage css={{ height: props.noImageHeight ? 'unset' : undefined }}>
            {props.image}
          </SWizardGridCardImage>
          {props.entityType && <Text variant={'bodyMedium'}>{props.entityType}</Text>}
        </HStack>

        <Spacer size={0.5} />

        <SWizardGridCardHeader>
          <SWizardGridCardTitle variant={'bodyLargeBold'} noLineClamp={props.noLineClamp}>
            {props.title}
          </SWizardGridCardTitle>
        </SWizardGridCardHeader>

        {props.subtitle ? (
          <>
            <Text variant={'bodyMedium'}>{props.subtitle}</Text>
            <Spacer size={pxToRem(12)} />
          </>
        ) : (
          <Spacer size={pxToRem(30)} />
        )}

        {props.status && (
          <>
            <CardStatus status={props.status} />
            <Spacer size={0.5} />
          </>
        )}

        <SWizardGridCardShareWrapper css={{ visibility: props.locked ? 'hidden' : 'visible' }}>
          <Text variant={'bodyMedium'}>{props.brief}</Text>
          {props.openMenu && (
            <Button
              renderAs="div"
              type="button"
              variant="text"
              tone="gray"
              fitContentVertically
              onClick={(event) => {
                event.stopPropagation()
                event.preventDefault()
                props.openMenu?.()
              }}
            >
              <Icon name="MoreInfo" size="1rem" />
            </Button>
          )}
        </SWizardGridCardShareWrapper>
      </SSecondaryButton>

      {props.locked && (
        <Link href={`/pricing`} legacyBehavior>
          <SWizardGridLockedWrapper>
            <HStack>
              <Icon name="LockClosed" color="var(--c-blue500)" />
              <Text css={{ color: 'var(--c-blue500)', fontWeight: 'bold' }}>Unlock content</Text>
            </HStack>
          </SWizardGridLockedWrapper>
        </Link>
      )}
    </SWizardGridCardWrapper>
  )
})
WizardGridCard.displayName = 'WizardGridCard'

export function WizardGridCardShare(props: {
  contacts?: components['schemas']['TrustedContactItemResponse'][] | { email: string }[]
  onShare?: () => void
}) {
  const { t } = useTranslation('trusted_contact')
  const getContactPresentation = (contact: AvatarOwner) => {
    if (!contact) {
      return null
    } else {
      return <Avatar size={40} owner={contact} />
    }
  }

  const hasContacts = !!props.contacts?.length
  const firstContact = props.contacts?.[0]
  const secondContact = props.contacts?.[1]
  const thirdContact = props.contacts?.[2]
  if (hasContacts) {
    if (props.contacts?.length === 1 && firstContact) {
      return (
        <SWizardGridCardShareItem>{getContactPresentation(firstContact)}</SWizardGridCardShareItem>
      )
    } else if (props.contacts?.length === 2 && firstContact && secondContact) {
      return (
        <SWizardGridCardShareArea>
          <SWizardGridCardShareItem css={{ zIndex: 1 }}>
            {getContactPresentation(firstContact)}
          </SWizardGridCardShareItem>
          <SWizardGridCardShareItem css={{ marginLeft: '-6px' }}>
            {getContactPresentation(secondContact)}
          </SWizardGridCardShareItem>
        </SWizardGridCardShareArea>
      )
    } else if (firstContact && secondContact && thirdContact) {
      return (
        <SWizardGridCardShareArea>
          <SWizardGridCardShareItem css={{ zIndex: 2 }}>
            {getContactPresentation(firstContact)}
          </SWizardGridCardShareItem>
          <SWizardGridCardShareItem css={{ zIndex: 1, marginLeft: '-6px' }}>
            {getContactPresentation(secondContact)}
          </SWizardGridCardShareItem>
          <SWizardGridCardShareItem css={{ marginLeft: '-6px' }}>
            {props.contacts?.length && props.contacts?.length >= 4
              ? `+${props.contacts?.length - 2}`
              : getContactPresentation(thirdContact)}
          </SWizardGridCardShareItem>
        </SWizardGridCardShareArea>
      )
    }
  }
  return props.onShare ? (
    <Button
      type="button"
      variant="round"
      onClick={(e) => {
        e.stopPropagation()
        e.preventDefault()
        props.onShare?.()
      }}
    >
      {t('trusted_contact.site.share_button')}
    </Button>
  ) : null
}
